import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';

export default Factory.extend({
  address: faker.location.streetAddress(),
  addressLineOne: null,
  admin: null,
  averageMonthlyVisitors: faker.number.int(10000),
  autoSignOutAtMinutesSinceMidnight: null,
  blocklistContacts: null,
  blocklistEnabled: false,
  ccReceptionistEnabled: false,
  city: null,
  companyId: 1,
  companyNameOverride: null,
  country: null,
  disabled: false,
  disabledToEmployeesAt: null,
  createdAt: faker.date.past(),
  currentVersion: '11.2.6',
  deliveriesOnboardingComplete: false,
  deviceNotificationsEnabled: false,
  emailNotificationEnabled: false,
  employeeScreeningEnabled: false,
  employeeScheduleEnabled: false,
  hostNotificationsEnabled: true,
  lastEntryAt: faker.date.past({ years: 10 }),
  latitude: null,
  locale: 'en',
  longitude: null,
  name: () => faker.company.name(),
  preRegistrationNotes: null,
  pubnubChannelDevices: 'devices',
  pubnubChannelIntegrations: (i) => `${i + 1}-integrations`,
  slackNotificationEnabled: false,
  smsNotificationEnabled: false,
  state: null,
  timezone: null,
  touchlessSigninEnabled: true,
  watchlist: Object.freeze({
    list: null,
    notifyEmail: null,
    notifyPhoneNumber: null,
  }),
  visitorGuideEnabled: false,
  visitorSurveyEnabled: false,
  zip: null,
  dashboardFields: () => [
    {
      name: 'NDA',
      componentName: 'plugin-activity-column',
    },
    {
      name: 'Notification',
      componentName: 'host-notification-column',
    },
    {
      name: 'Signed In',
      componentName: 'signed-in-column',
    },
    {
      name: 'Signed Out',
      componentName: 'signed-out-column',
    },
    { name: 'Connect Notification', componentName: 'connect-notification-column' },
    { name: 'Shared Notes', componentName: 'shared-notes-column' },
    { name: 'Arrived At', componentName: 'arrived-at-column' },
  ],
  deliveryPersonId: null,
  enabledLocales: () => ['es', 'fr'],
  hostApprovalEnabled: false,

  inviteDashboardFields: () => [
    { name: 'Private Notes' },
    { name: 'Been Here Before?' },
    { name: 'NDA' },
    { name: 'Due At' },
    { name: 'Signed In' },
    { name: 'Connect Notification', componentName: 'connect-notification-column' },
    { name: 'Shared Notes', componentName: 'shared-notes-column' },
    { name: 'Arrived At', componentName: 'arrived-at-column' },
  ],
  capacityLimit: null,
  capacityNotificationRecipients: () => [],
  masterLocationId: null,
  multipleLanguagesEnabled: true,
  nearVisitScreeningEnabled: false,
  preRegistrationEnabled: true,
  preRegistrationLink: 'https://envoy.com/invites/token_a',
  preRegistrationRequiredEnabled: false,
  pubnubChannelEntry: 'a',
  pubnubChannelInvite: 'b',
  securityDeskLink: 'https://envoy.com/security/token_a',
  updateAvailable: false,
  scheduleEligibilityOffset: null,

  userDocumentRequirements: () => [],

  traitRoomsStandardSubscription: trait({
    afterCreate(location, server) {
      const billingCompany = server.schema.billingCompanies.first();
      const company = server.schema.companies.first();
      server.create('location-subscription', 'traitRoomsStandard', { location, billingCompany, company });
    },
  }),

  traitRoomsStandardSubscriptionOnTrial: trait({
    afterCreate(location, server) {
      const billingCompany = server.schema.billingCompanies.first();
      const company = server.schema.companies.first();
      server.create('location-subscription', 'traitRoomsStandard', 'traitOnTrial', {
        location,
        billingCompany,
        company,
      });
    },
  }),

  traitVisitorsPremiumSubscriptionOnTrial: trait({
    afterCreate(location, server) {
      const billingCompany = server.schema.billingCompanies.first();
      const company = server.schema.companies.first();
      server.create('location-subscription', 'traitVisitorsPremium', 'traitOnTrial', {
        location,
        billingCompany,
        company,
      });
    },
  }),

  withConfig: trait({
    afterCreate(location, server) {
      server.create('config', {
        location,
        localizedFields: [
          {
            field: 'Your Full Name',
            localized: 'Your Full Name',
          },
          {
            field: 'Your Email Address',
            localized: 'Your Email Address',
          },
          {
            field: 'Your Phone Number',
            localized: 'Your Phone Number',
          },
          {
            field: 'Host',
            localized: 'Host',
          },
          {
            field: 'Purpose of visit',
            localized: 'Purpose of visit',
          },
        ],
      });
    },
  }),

  withNoDocumentApprovalRequirement: trait({
    afterCreate(location, server) {
      const vaxTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-vaccine-card' }) ||
        server.create('user-document-template', 'asCovidVaccineCard');
      const vaxConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: vaxTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      const testTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-test-result' }) ||
        server.create('user-document-template', 'asCovidTestResult');
      const testConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: testTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      location.update({
        userDocumentRequirements: [
          {
            'required-minimum': 0,
            'user-document-template-configuration-ids': [
              parseInt(vaxConfiguration.id, 10),
              parseInt(testConfiguration.id, 10),
            ],
          },
        ],
      });
    },
  }),

  withOnlyVaxDocumentApprovalRequirement: trait({
    afterCreate(location, server) {
      const vaxTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-vaccine-card' }) ||
        server.create('user-document-template', 'asCovidVaccineCard');
      const vaxConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: vaxTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      location.update({
        userDocumentRequirements: [
          {
            'required-minimum': 1,
            'user-document-template-configuration-ids': [parseInt(vaxConfiguration.id, 10)],
          },
        ],
      });
    },
  }),

  withOnlyTestDocumentApprovalRequirement: trait({
    afterCreate(location, server) {
      const testTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-test-result' }) ||
        server.create('user-document-template', 'asCovidTestResult');
      const testConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: testTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      location.update({
        userDocumentRequirements: [
          {
            'required-minimum': 1,
            'user-document-template-configuration-ids': [parseInt(testConfiguration.id, 10)],
          },
        ],
      });
    },
  }),

  withEitherDocumentApprovalRequirement: trait({
    afterCreate(location, server) {
      const vaxTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-vaccine-card' }) ||
        server.create('user-document-template', 'asCovidVaccineCard');
      const vaxConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: vaxTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      const testTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-test-result' }) ||
        server.create('user-document-template', 'asCovidTestResult');
      const testConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: testTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      location.update({
        userDocumentRequirements: [
          {
            'required-minimum': 1,
            'user-document-template-configuration-ids': [
              parseInt(vaxConfiguration.id, 10),
              parseInt(testConfiguration.id, 10),
            ],
          },
        ],
      });
    },
  }),

  withBothDocumentApprovalRequirement: trait({
    afterCreate(location, server) {
      const vaxTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-vaccine-card' }) ||
        server.create('user-document-template', 'asCovidVaccineCard');
      const vaxConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: vaxTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      const testTemplate =
        server.schema.userDocumentTemplates.findBy({ identifier: 'covid-19-test-result' }) ||
        server.create('user-document-template', 'asCovidTestResult');
      const testConfiguration = server.create('user-document-template-configuration', {
        userDocumentTemplate: testTemplate,
        userDocumentTemplateConfigurableId: { id: location.id, type: 'location' },
        active: true,
      });

      location.update({
        userDocumentRequirements: [
          {
            'required-minimum': 2,
            'user-document-template-configuration-ids': [
              parseInt(vaxConfiguration.id, 10),
              parseInt(testConfiguration.id, 10),
            ],
          },
        ],
      });
    },
  }),

  withDisabledToEmployeesAtField: trait({
    afterCreate(location) {
      location.update({
        disabledToEmployeesAt: new Date(),
      });
    },
  }),
});
